// Перевод интерфейса по русскому тексту.
//
// Строки интерфейса пишутся в коде и разметке по-русски. Английский берётся
// из двух словарей: ключевого (i18n.ts, t(locale, key)) и текстового
// (locales/ru-en.json: русская строка → английская, с подстановками {0}…).
// Текстовый словарь работает двумя путями:
//   - tr()/trCount() — явно, где текст рисуется не в DOM (холст ECharts)
//     или собирается из частей;
//   - перевод DOM: при locale=en текстовые узлы и атрибуты (title,
//     aria-label, placeholder, data-tooltip, alt) переводятся при появлении
//     и изменении (MutationObserver), при возврате к ru — восстанавливаются.
// Строки, которых нет в словарях, остаются русскими — не транслитом.
// Полноту словаря проверяет scripts/tests/test_ui_i18n_coverage.py.

import catalog from './locales/ru-en.json';
import { dictionaryPairs } from './i18n';
import type { Locale } from './types';

const CYRILLIC = /[А-Яа-яЁё]/;
const TRANSLATED_ATTRIBUTES = ['title', 'aria-label', 'placeholder', 'data-tooltip', 'alt'] as const;

interface PatternEntry {
  /** Самый длинный неизменный кусок шаблона — дешёвый отсев до регулярки. */
  anchor: string;
  regex: RegExp;
  english: string;
}

let uiLocale: Locale = 'ru';
const exact = new Map<string, string>();
/** Данные (единицы, значения разрезов) — отдельно: «Единица» в данных — «units», в заголовке — «Unit». */
const dataExact = new Map<string, string>();
const templates = new Map<string, string>();
const patterns: PatternEntry[] = [];

function normalizeSpace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function register(russian: string, english: string): void {
  const key = normalizeSpace(russian);
  if (!key || !CYRILLIC.test(key)) return;
  if (/\{\d+\}/.test(key)) {
    if (templates.has(key)) return;
    templates.set(key, english);
    const chunks = key.split(/\{\d+\}/);
    const anchor = chunks.reduce((longest, chunk) => (chunk.length > longest.length ? chunk : longest), '');
    const source = key.split(/(\{\d+\})/).map((part) => (/^\{\d+\}$/.test(part) ? '(.+?)' : escapeRegex(part))).join('');
    patterns.push({ anchor, regex: new RegExp(`^${source}$`, 's'), english });
    return;
  }
  if (!exact.has(key)) exact.set(key, english);
}

dictionaryPairs().forEach(([russian, english]) => register(russian, english));
Object.entries(catalog.ui as Record<string, string>).forEach(([russian, english]) => register(russian, english));
// Словарь данных — только для явного trData(): в DOM переводится лишь
// интерфейс, а не названия из данных и не заголовки, введённые пользователем.
Object.entries(catalog.data as Record<string, string>).forEach(([russian, english]) => {
  dataExact.set(normalizeSpace(russian), english);
});
// Длинные шаблоны раньше коротких: «{0} · муниципальная статистика» точнее «{0} · {1}».
patterns.sort((left, right) => right.anchor.length - left.anchor.length);

export function setUiLocale(locale: Locale): void {
  uiLocale = locale;
  if (locale === 'en') startDomTranslation();
  else stopDomTranslation();
}

export function currentUiLocale(): Locale {
  return uiLocale;
}

function fill(template: string, values: ReadonlyArray<string | number>): string {
  return template.replace(/\{(\d+)\}/g, (match, index: string) => {
    const value = values[Number(index)];
    return value == null ? match : String(value);
  });
}

/** Английский для русской строки без учёта текущего языка; null — перевода нет. */
export function englishFor(text: string): string | null {
  const key = normalizeSpace(text);
  if (!key || !CYRILLIC.test(key)) return null;
  const direct = exact.get(key);
  if (direct != null) return direct;
  for (const pattern of patterns) {
    if (pattern.anchor && !key.includes(pattern.anchor)) continue;
    const match = pattern.regex.exec(key);
    if (!match) continue;
    // Подстановки — обычно данные; переводятся, только если это целиком
    // известная строка («проверено», единица измерения).
    const values = match.slice(1).map((value) => exact.get(normalizeSpace(value)) ?? value);
    return fill(pattern.english, values);
  }
  return null;
}

/**
 * Строка интерфейса на текущем языке. Ключ — русский текст или шаблон
 * с {0}…; values подставляются в оба варианта.
 */
export function tr(russian: string, ...values: Array<string | number>): string {
  if (uiLocale === 'en') {
    const key = normalizeSpace(russian);
    const template = values.length ? templates.get(key) : undefined;
    if (template != null) return fill(template, values);
    const english = exact.get(key);
    if (english != null) return values.length ? fill(english, values) : english;
  }
  return values.length ? fill(russian, values) : russian;
}

/** «1 наблюдение», «3 наблюдения», «5 наблюдений» / «1 observation», «5 observations». */
export function trCount(count: number, one: string, few: string, many: string): string {
  const mod10 = Math.abs(count) % 10;
  const mod100 = Math.abs(count) % 100;
  const russian = mod10 === 1 && mod100 !== 11
    ? one
    : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)
      ? few
      : many;
  if (uiLocale === 'en') return `${count} ${tr(count === 1 ? one : many)}`;
  return `${count} ${russian}`;
}

/** Значение из данных (единица, «Всего») — перевод, если он есть в словаре, иначе как есть. */
export function trData(value: string): string {
  if (uiLocale !== 'en' || !value) return value;
  return dataTranslation(value) ?? value;
}

/** Английский вариант строки данных по словарю — при любом языке интерфейса; null, если перевода нет. */
export function dataTranslation(value: string): string | null {
  if (!value) return null;
  const key = normalizeSpace(value);
  return dataExact.get(key) ?? exact.get(key) ?? null;
}

// --- Перевод DOM ------------------------------------------------------------

interface TextRecord { node: WeakRef<Text>; russian: string; english: string }
interface AttributeRecord { element: WeakRef<Element>; name: string; russian: string; english: string }

const textRecords: TextRecord[] = [];
const attributeRecords: AttributeRecord[] = [];
let observer: MutationObserver | null = null;

function skipped(element: Element | null): boolean {
  if (!element) return true;
  const tag = element.tagName;
  if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEXTAREA') return true;
  // data-i18n-skip — заголовки дашбордов и виджетов: их пишет пользователь
  // (или шаблон), и частичный перевод совпавших слов только путает.
  return element.closest('[data-i18n-skip], [translate="no"], [contenteditable="true"]') != null;
}

function translateText(node: Text): void {
  const text = node.data;
  if (!CYRILLIC.test(text) || skipped(node.parentElement)) return;
  const english = englishFor(text);
  if (english == null) return;
  const leading = /^\s*/.exec(text)?.[0] ?? '';
  const trailing = /\s*$/.exec(text)?.[0] ?? '';
  const translated = `${leading}${english}${trailing}`;
  if (translated === text) return;
  textRecords.push({ node: new WeakRef(node), russian: text, english: translated });
  node.data = translated;
}

function translateAttribute(element: Element, name: string): void {
  const value = element.getAttribute(name);
  if (!value || !CYRILLIC.test(value) || skipped(element)) return;
  const english = englishFor(value);
  if (english == null || english === value) return;
  attributeRecords.push({ element: new WeakRef(element), name, russian: value, english });
  element.setAttribute(name, english);
}

function translateElement(element: Element): void {
  TRANSLATED_ATTRIBUTES.forEach((name) => {
    if (element.hasAttribute(name)) translateAttribute(element, name);
  });
}

/** Переводит поддерево сразу (без ожидания наблюдателя). */
export function translateTree(root: Node): void {
  if (uiLocale !== 'en') return;
  if (root.nodeType === Node.TEXT_NODE) {
    translateText(root as Text);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;
  if (root.nodeType === Node.ELEMENT_NODE) {
    if (skipped(root as Element)) return;
    translateElement(root as Element);
  }
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeType === Node.TEXT_NODE) translateText(node as Text);
    else translateElement(node as Element);
  }
}

function compactRecords(): void {
  // Узлы, удалённые из документа, держать незачем.
  if (textRecords.length > 5000) {
    const alive = textRecords.filter((record) => record.node.deref()?.isConnected);
    textRecords.splice(0, textRecords.length, ...alive);
  }
  if (attributeRecords.length > 5000) {
    const alive = attributeRecords.filter((record) => record.element.deref()?.isConnected);
    attributeRecords.splice(0, attributeRecords.length, ...alive);
  }
}

function startDomTranslation(): void {
  if (typeof document === 'undefined' || !document.body) return;
  translateTree(document.body);
  if (observer) return;
  observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') mutation.addedNodes.forEach((node) => translateTree(node));
      else if (mutation.type === 'characterData') translateText(mutation.target as Text);
      else if (mutation.type === 'attributes' && mutation.attributeName) translateAttribute(mutation.target as Element, mutation.attributeName);
    });
    compactRecords();
  });
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: [...TRANSLATED_ATTRIBUTES],
  });
}

function stopDomTranslation(): void {
  observer?.disconnect();
  observer = null;
  // Возврат к русскому: восстанавливается то, что перевод заменил и с тех пор
  // не переписал сам код.
  textRecords.forEach(({ node, russian, english }) => {
    const target = node.deref();
    if (target && target.data === english) target.data = russian;
  });
  attributeRecords.forEach(({ element, name, russian, english }) => {
    const target = element.deref();
    if (target && target.getAttribute(name) === english) target.setAttribute(name, russian);
  });
  textRecords.length = 0;
  attributeRecords.length = 0;
}
